import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, ChevronDown, ChevronRight, Search, ArrowUpDown, ExternalLink, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "./ui/table";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

/**
 * Family AUM Leaderboard panel.
 * Originally lived under the Dashboard's "Family AUM" tab — moved here
 * (next to "All Clients") at user's request so the family-roll-up sits
 * alongside per-client summary in the Client-wise page.
 */
export default function FamilyAumPanel({ token }) {
  const navigate = useNavigate();
  const [familyAum, setFamilyAum] = useState(null);
  const [familyAumLoading, setFamilyAumLoading] = useState(false);
  const [familySearch, setFamilySearch] = useState("");
  const [familySort, setFamilySort] = useState({ column: "total_market", direction: "desc" });
  const [expandedFamily, setExpandedFamily] = useState(null);
  const [familyMinMembers, setFamilyMinMembers] = useState(1);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setFamilyAumLoading(true);
      try {
        const resp = await fetch(
          `${API_URL}/api/dashboard/family-aum?limit=5000&min_members=${familyMinMembers}`,
          { headers: { "Authorization": `Bearer ${token}` } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setFamilyAum(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load family AUM", { description: String(e) });
      } finally {
        if (!cancelled) setFamilyAumLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [familyMinMembers, token]);

  const sortedRows = useMemo(() => {
    const rows = (familyAum?.families || []).filter((f) => {
      if (!familySearch) return true;
      const q = familySearch.toLowerCase();
      return (f.group_head_name || "").toLowerCase().includes(q)
        || (f.group_head_pan || "").toLowerCase().includes(q)
        || (f.associate_code || "").toLowerCase().includes(q)
        || (f.associate_name || "").toLowerCase().includes(q);
    });
    const sortKey = familySort.column;
    const sortDir = familySort.direction === "asc" ? 1 : -1;
    return rows.slice().sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "string") return sortDir * String(va || "").localeCompare(String(vb || ""));
      return sortDir * ((va || 0) - (vb || 0));
    });
  }, [familyAum, familySearch, familySort]);

  const exportCsv = () => {
    if (!sortedRows.length) {
      toast.info("No rows to export");
      return;
    }
    const header = ["Rank", "Group Head", "PAN", "Associate Code", "Associate Name", "Members", "Combined AUM", "Combined Cost", "P/L", "Weighted XIRR %", "Holdings"];
    const lines = [header.join(",")];
    for (const r of sortedRows) {
      lines.push([
        r.rank,
        `"${(r.group_head_name || "").replace(/"/g, '""')}"`,
        r.group_head_pan,
        r.associate_code || "",
        `"${(r.associate_name || "").replace(/"/g, '""')}"`,
        r.member_count,
        r.total_market,
        r.total_cost,
        r.total_profit_loss,
        r.weighted_xirr_pct,
        r.holdings_count,
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Family_AUM_Leaderboard_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  return (
    <Card className="border-amber-200">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-600" />
              Family AUM Leaderboard
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {familyAum
                ? `${familyAum.count} families · ₹${(familyAum.total_aum / 10000000).toFixed(2)} Cr combined AUM`
                : "Group Heads ranked by combined family book — quick retention signal."}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-600">Min members:</label>
              <Input
                type="number"
                min={1}
                max={20}
                value={familyMinMembers}
                onChange={(e) => setFamilyMinMembers(Math.max(1, parseInt(e.target.value || "1", 10)))}
                className="h-8 w-16 text-xs"
                data-testid="family-min-members"
              />
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-3 w-3 text-gray-400" />
              <Input
                placeholder="Search head name or PAN…"
                value={familySearch}
                onChange={(e) => setFamilySearch(e.target.value)}
                className="h-8 w-56 pl-7 text-xs"
                data-testid="family-search"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              className="gap-2"
              data-testid="family-aum-export"
            >
              <Download className="h-4 w-4" />
              CSV
            </Button>
          </div>
        </div>

        {familyAumLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
            <span className="ml-3 text-sm text-gray-500">Loading families…</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-amber-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-amber-100"
                    onClick={() => setFamilySort({
                      column: "group_head_name",
                      direction: familySort.column === "group_head_name" && familySort.direction === "asc" ? "desc" : "asc",
                    })}
                  >
                    Group Head <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-amber-100"
                    onClick={() => setFamilySort({
                      column: "associate_code",
                      direction: familySort.column === "associate_code" && familySort.direction === "asc" ? "desc" : "asc",
                    })}
                  >
                    Associate <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-amber-100"
                    onClick={() => setFamilySort({
                      column: "member_count",
                      direction: familySort.column === "member_count" && familySort.direction === "desc" ? "asc" : "desc",
                    })}
                  >
                    Members <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-amber-100"
                    onClick={() => setFamilySort({
                      column: "total_market",
                      direction: familySort.column === "total_market" && familySort.direction === "desc" ? "asc" : "desc",
                    })}
                  >
                    Combined AUM <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-amber-100"
                    onClick={() => setFamilySort({
                      column: "total_profit_loss",
                      direction: familySort.column === "total_profit_loss" && familySort.direction === "desc" ? "asc" : "desc",
                    })}
                  >
                    P/L <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-amber-100"
                    onClick={() => setFamilySort({
                      column: "weighted_xirr_pct",
                      direction: familySort.column === "weighted_xirr_pct" && familySort.direction === "desc" ? "asc" : "desc",
                    })}
                  >
                    XIRR % <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-50" />
                  </TableHead>
                  <TableHead className="w-32 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((f) => {
                  const isOpen = expandedFamily === f.group_head_pan;
                  const xirrColor = (f.weighted_xirr_pct || 0) >= 12
                    ? "text-green-700"
                    : (f.weighted_xirr_pct || 0) >= 8
                      ? "text-amber-700"
                      : "text-red-700";
                  const plColor = (f.total_profit_loss || 0) >= 0 ? "text-green-700" : "text-red-700";
                  return (
                    <React.Fragment key={f.group_head_pan}>
                      <TableRow
                        className={`hover:bg-amber-50/40 ${isOpen ? "bg-amber-50/30" : ""}`}
                        data-testid={`family-row-${f.group_head_pan}`}
                      >
                        <TableCell>
                          <button
                            onClick={() => setExpandedFamily(isOpen ? null : f.group_head_pan)}
                            className="text-gray-500 hover:text-amber-600"
                            data-testid={`family-expand-${f.group_head_pan}`}
                          >
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </TableCell>
                        <TableCell className="font-semibold text-gray-700">#{f.rank}</TableCell>
                        <TableCell>
                          <div
                            className="font-medium text-gray-800 cursor-help"
                            title={`PAN: ${f.group_head_pan}`}
                            data-testid={`family-head-name-${f.group_head_pan}`}
                          >
                            {f.group_head_name || "—"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {f.associate_code ? (
                            <div
                              className="text-xs"
                              title={f.associate_name || ""}
                              data-testid={`family-associate-${f.group_head_pan}`}
                            >
                              <Badge variant="outline" className="font-mono border-teal-300 text-teal-700 bg-teal-50">
                                {f.associate_code}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50">
                            {f.member_count}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(f.total_market)}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${plColor}`}>
                          {formatCurrency(f.total_profit_loss)}
                        </TableCell>
                        <TableCell className={`text-right font-semibold ${xirrColor}`}>
                          {(f.weighted_xirr_pct || 0).toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px] gap-1"
                            onClick={() => navigate(`/portfolio?client=${encodeURIComponent(f.group_head_pan)}`)}
                            data-testid={`family-open-portfolio-${f.group_head_pan}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            Portfolio
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-amber-50/20">
                          <TableCell colSpan={9} className="py-3">
                            <div className="ml-10 mr-4">
                              <div className="text-[11px] font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                                Members breakdown · {f.holdings_count} holdings
                              </div>
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-white/60">
                                    <TableHead className="h-8 text-[11px]">Member</TableHead>
                                    <TableHead className="h-8 text-[11px] text-right">Cost</TableHead>
                                    <TableHead className="h-8 text-[11px] text-right">Market</TableHead>
                                    <TableHead className="h-8 text-[11px] text-right">P/L</TableHead>
                                    <TableHead className="h-8 text-[11px] text-right">XIRR %</TableHead>
                                    <TableHead className="h-8 text-[11px] text-right">Holdings</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(f.members || []).map((m) => (
                                    <TableRow key={m.pan} className="text-xs">
                                      <TableCell>
                                        <div className="font-medium text-gray-800">{m.name || "—"}</div>
                                        <div className="text-[10px] font-mono text-gray-400">{m.pan}</div>
                                      </TableCell>
                                      <TableCell className="text-right">{formatCurrency(m.cost)}</TableCell>
                                      <TableCell className="text-right font-medium">{formatCurrency(m.market)}</TableCell>
                                      <TableCell className={`text-right ${(m.profit_loss || 0) >= 0 ? "text-green-700" : "text-red-700"}`}>
                                        {formatCurrency(m.profit_loss)}
                                      </TableCell>
                                      <TableCell className="text-right">{(m.weighted_xirr_pct || 0).toFixed(2)}%</TableCell>
                                      <TableCell className="text-right">{m.holdings_count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {sortedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-gray-400">
                      No families found. Map clients to a Group Head from the Client-wise summary to populate this leaderboard.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
